import React, { useMemo } from 'react';
import {
  TrendingUp,
  Coins,
  CircleDot,
  Zap,
  Sparkles,
  Image as ImageIcon,
  Droplets,
  ArrowLeftRight,
  LayoutDashboard,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import { getThresholdUi } from '../utils/taxRules';
import { fmtEurSummary } from '../utils/formatNumbers';
import { resolveUkSummaryForDisplay } from '../utils/ukSummaryTotals.js';
import { formatTaxYearLabel } from '../utils/taxYear.js';

export default function Dashboard({
  vetTotalProfit,
  vthoTotalProfit,
  b3trTotalProfit,
  nftTotalProfit,
  exchangeTotalProfit,
  liquidityTotalProfit,
  taxDataset = null,
  selectedCountry = 'DE',
  selectedYear = 2025,
  pdfMode = false,
  embedded = false,
  showHeader = true,
  compact = false,
}) {
  const { language } = useLanguage();
  const displayYearLabel = formatTaxYearLabel(selectedYear, selectedCountry);

  // Hooks müssen vor jedem frühen return laufen (pdfMode vs. UI teilen dieselbe Komponente).
  const ui = useMemo(() => {
    const noMotion = "motion-reduce:transition-none motion-reduce:transform-none";

    const shineBase =
      "after:content-[''] after:absolute after:inset-0 after:pointer-events-none after:opacity-0 " +
      "after:bg-gradient-to-r after:from-transparent after:via-white/20 after:to-transparent " +
      "after:-translate-x-[140%] after:skew-x-[-12deg] " +
      `after:transition-all after:duration-700 after:ease-out ${noMotion} `;

    return {
      shell:
        "relative min-h-[560px] overflow-hidden " +
        "bg-gradient-to-br from-slate-100/95 via-white to-indigo-50/35 " +
        "before:pointer-events-none before:absolute before:inset-0 before:bg-[radial-gradient(ellipse_120%_80%_at_100%_-20%,rgba(99,102,241,0.08),transparent_50%)] " +
        "after:pointer-events-none after:absolute after:inset-0 after:bg-[radial-gradient(ellipse_80%_60%_at_0%_100%,rgba(16,185,129,0.06),transparent_45%)]",

      embeddedFrame:
        "rounded-[1.65rem] border border-slate-200/80 bg-white/90 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.22)] ring-1 ring-slate-900/[0.05] backdrop-blur-sm overflow-hidden",

      summaryCard:
        "group relative overflow-hidden rounded-2xl p-6 sm:p-7 text-white shadow-xl " +
        "ring-1 ring-white/25 shadow-indigo-950/10 " +
        "transition-all duration-300 ease-out " +
        "hover:-translate-y-0.5 hover:shadow-2xl hover:ring-white/35 " +
        "focus-within:-translate-y-0.5 focus-within:shadow-2xl focus-within:ring-white/40 " +
        noMotion +
        " " +
        shineBase +
        "group-hover:after:opacity-100 group-hover:after:translate-x-[140%] " +
        "focus-within:after:opacity-100 focus-within:after:translate-x-[140%]",

      summaryIconWrap:
        "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/18 ring-1 ring-white/25 " +
        "shadow-inner transition-transform duration-300 group-hover:scale-105 " +
        noMotion,

      summaryValue:
        "text-3xl sm:text-[2rem] font-semibold tracking-tight tabular-nums leading-none " +
        "drop-shadow-sm transition-transform duration-300 ease-out " +
        "group-hover:scale-[1.01] origin-left " +
        noMotion,

      tokenWrap:
        "group relative rounded-2xl p-[1px] " +
        "bg-gradient-to-br from-slate-200/80 via-slate-100/60 to-indigo-200/50 " +
        "shadow-sm transition-all duration-300 ease-out " +
        "hover:from-indigo-200/70 hover:via-slate-100/50 hover:to-emerald-200/45 " +
        "hover:shadow-md focus-within:from-indigo-200/80 focus-within:via-slate-100/55 focus-within:to-emerald-200/50 " +
        noMotion,

      tokenCard:
        "relative overflow-hidden rounded-[0.95rem] bg-white p-5 sm:p-6 " +
        "shadow-[inset_0_1px_0_0_rgba(255,255,255,0.85)] " +
        "ring-1 ring-slate-900/[0.06] " +
        "transition-all duration-300 ease-out " +
        "hover:-translate-y-0.5 hover:shadow-lg hover:ring-slate-900/10 " +
        "focus-within:-translate-y-0.5 focus-within:shadow-lg focus-within:ring-slate-900/10 " +
        noMotion +
        " pl-5 before:absolute before:left-0 before:top-4 before:bottom-4 before:w-1 before:rounded-full " +
        "before:bg-gradient-to-b before:from-indigo-500 before:to-violet-500 before:opacity-90 " +
        "before:content-[''] " +
        "before:transition-opacity before:duration-300 group-hover:before:opacity-100",

      tokenTitleRow: "mb-4 flex items-start justify-between gap-3",

      tokenIconWrap:
        "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-indigo-600 " +
        "ring-1 ring-slate-200/80 transition-colors duration-300 group-hover:bg-indigo-50 group-hover:text-indigo-700 " +
        noMotion,

      badge:
        "relative inline-flex items-center gap-2 text-sm text-slate-600 " +
        "rounded-lg border border-slate-200/80 bg-slate-50/90 px-2.5 py-1 " +
        "transition-all duration-300 ease-out " +
        "group-hover:border-slate-300 group-hover:bg-slate-100 " +
        noMotion,

      badgeDot:
        "h-2 w-2 rounded-full bg-slate-300 transition-all duration-300 ease-out " +
        "group-hover:bg-slate-400 group-hover:scale-110 " +
        noMotion,

      tooltip:
        "pointer-events-none absolute -bottom-11 right-0 z-10 " +
        "rounded-lg bg-slate-900 px-3 py-2 text-xs text-white shadow-lg " +
        "opacity-0 translate-y-1 scale-[0.98] " +
        "transition-all duration-200 ease-out " +
        "group-hover:opacity-100 group-hover:translate-y-0 group-hover:scale-100 " +
        noMotion,

      tooltipArrow: "absolute -top-1.5 right-3 h-3 w-3 rotate-45 bg-slate-900",

      row:
        "group/row flex items-center justify-between gap-3 " +
        "-mx-1.5 rounded-lg px-2 py-2.5 " +
        "transition-all duration-200 ease-out " +
        "hover:bg-slate-50/90 " +
        noMotion,

      rowDivider:
        "border-b border-slate-100 transition-colors duration-300 group-hover:border-slate-200/80 " + noMotion,

      valueBlue:
        "shrink-0 whitespace-nowrap text-right text-[15px] font-semibold tabular-nums text-indigo-600 " +
        "transition-all duration-200 ease-out " +
        "group-hover:text-indigo-700 group-hover:translate-x-0.5 " +
        noMotion,

      valueGreen:
        "shrink-0 whitespace-nowrap text-right text-[15px] font-semibold tabular-nums text-emerald-600 " +
        "transition-all duration-200 ease-out " +
        "group-hover:text-emerald-700 group-hover:translate-x-0.5 " +
        noMotion,
    };
  }, []);

  // ✅ E1: categoryText Helper (mit korrigierten IT-Labels)
  const cc = String(selectedCountry || 'DE').toUpperCase();
  const categoryText = (key) => {
    const map = {
      DE: {
        disposal: {
          en: "Disposal transactions (German Income Tax Act: §23 EStG)",
          de: "Veräußerungsgeschäfte (§23 EStG)",
        },
        other: {
          en: "Other income (German Income Tax Act: §22 No. 3 EStG)",
          de: "Sonstige Einkünfte (§22 Nr. 3 EStG)",
        },
        native: null,
      },
      IT: {
        disposal: { en: 'Crypto capital gains/losses (Italy: Quadro RT)', de: 'Krypto-Plusvalenzen/Minusvalenzen (Italien: Quadro RT)' },
        other: { en: 'Crypto proceeds (Staking/Airdrops/Rewards) (Italy: Quadro RT)', de: 'Krypto-Erträge (Staking/Airdrops/Rewards) (Italien: Quadro RT)' },
        native: { disposal: 'Plusvalenze e minusvalenze da cripto-attività – Quadro RT', other: 'Altri proventi da cripto-attività (staking/ricompense) – Quadro RT' }
      },
      GR: {
        disposal: { en: 'Income from capital transfer gains (Greece: E1 Table 4E)', de: 'Einkünfte aus Kapitalübertragungsgewinnen (Griechenland: E1 Tabelle 4E)' },
        other: { en: 'Other crypto income (staking/rewards/airdrops) (Greece)', de: 'Sonstige Krypto-Einkünfte (Staking/Rewards/Airdrops) (Griechenland)' },
        native: { disposal: 'Εισόδημα από υπεραξία μεταβίβασης κεφαλαίου (Ε1 Πίνακας 4Ε)', other: 'Λοιπά εισοδήματα από κρυπτοστοιχεία (staking/rewards/airdrops)' }
      },
      US: {
        disposal: { en: 'Capital gains/losses (Form 8949, Schedule D)', de: 'Kapitalgewinne/-Verluste (Form 8949, Schedule D)' },
        other: { en: 'Ordinary income (Form 1040, Schedule 1)', de: 'Gewöhnliches Einkommen (Form 1040, Schedule 1)' },
        native: null
      },
      UK: {
        disposal: {
          en: 'Capital gains/losses (UK: SA108 / Cryptoasset Self Assessment)',
          de: 'Kapitalgewinne/-verluste (UK: SA108 / Cryptoasset Self Assessment)',
        },
        other: {
          en: 'Miscellaneous income (staking/rewards/airdrops)',
          de: 'Miscellaneous income (Staking/Rewards/Airdrops)',
        },
        native: null,
      },
      GB: {
        disposal: {
          en: 'Capital gains/losses (UK: SA108 / Cryptoasset Self Assessment)',
          de: 'Kapitalgewinne/-verluste (UK: SA108 / Cryptoasset Self Assessment)',
        },
        other: {
          en: 'Miscellaneous income (staking/rewards/airdrops)',
          de: 'Miscellaneous income (Staking/Rewards/Airdrops)',
        },
        native: null,
      },
    };
    const entry = map[cc] || map.DE;
    /*
     * UI-Sprache entscheidet die Beschreibung. Country bestimmt nur das Steuerrecht
     * (z. B. §23 EStG bei DE, Form 8949/Schedule D bei US, Quadro RT bei IT).
     */
    const primaryLang = String(language || "").toLowerCase().startsWith("en") ? "en" : "de";
    const primary =
      entry[key]?.[primaryLang] ||
      entry[key]?.en ||
      entry[key]?.de ||
      key;
    const secondary = entry.native?.[key] || null;
    return { primary, secondary };
  };

  // ✅ Translation helper: fallback, wenn t() nur den key zurückgibt
  const tr = (key, fallback) => {
    const val = t(key, language);
    return (!val || val === key) ? fallback : val;
  };

  // 🔧 Safe handling of profit data
  const normalizeTotals = (v, { numberMeansDisposal = false } = {}) => {
    if (typeof v === 'object' && v) {
      return {
        swapProfit: Number(v.swapProfit || 0),
        otherIncome: Number(v.otherIncome || 0),
        total: Number(v.total || 0)
      };
    }
    const n = Number(v || 0);
    return {
      swapProfit: numberMeansDisposal ? n : 0,
      otherIncome: 0,
      total: n
    };
  };

  const safeVet = normalizeTotals(vetTotalProfit);
  const safeVtho = normalizeTotals(vthoTotalProfit);
  const safeB3tr = normalizeTotals(b3trTotalProfit);
  const safeExchange = normalizeTotals(exchangeTotalProfit, { numberMeansDisposal: true });
  const safeLiquidity = normalizeTotals(liquidityTotalProfit, { numberMeansDisposal: true });
  const safeNft = typeof nftTotalProfit === 'number' ? Number(nftTotalProfit || 0) : 0;

  const legacyTotalSwapProfit =
    safeVet.swapProfit +
    safeVtho.swapProfit +
    safeB3tr.swapProfit +
    safeNft +
    safeLiquidity.swapProfit +
    safeExchange.swapProfit;

  const legacyTotalOtherIncome =
    safeVet.otherIncome +
    safeVtho.otherIncome +
    safeB3tr.otherIncome +
    safeExchange.otherIncome;

  // 💱 Currency/UI: EUR default, USD for US (cc already declared above)
  const isUS = cc === 'US' || cc === 'USA' || cc.includes('UNITED STATES');
  const isUK = cc === 'UK' || cc === 'GB' || cc.includes('UNITED KINGDOM') || cc.includes('UNITED KINGDOM');

  const ukSummary = isUK
    ? resolveUkSummaryForDisplay(taxDataset || {}, selectedYear, {
        language,
        fiatCurrency: 'GBP',
      })
    : null;

  const ukSections = ukSummary?.sectionBreakdown || null;

  const pickUkSection = (key, field, legacyValue) =>
    isUK && ukSections?.[key] != null
      ? Number(ukSections[key][field] || 0)
      : legacyValue;

  const displayVetDisp = pickUkSection('VET', 'swapProfit', safeVet.swapProfit);
  const displayVetInc = pickUkSection('VET', 'otherIncome', safeVet.otherIncome);
  const displayVthoDisp = pickUkSection('VTHO', 'swapProfit', safeVtho.swapProfit);
  const displayVthoInc = pickUkSection('VTHO', 'otherIncome', safeVtho.otherIncome);
  const displayB3trDisp = pickUkSection('VIP-180', 'swapProfit', safeB3tr.swapProfit);
  const displayB3trInc = pickUkSection('VIP-180', 'otherIncome', safeB3tr.otherIncome);
  const displayNftDisp = pickUkSection('NFT', 'swapProfit', safeNft);
  const displayNftInc = pickUkSection('NFT', 'otherIncome', 0);
  const displayLiqDisp = pickUkSection('LIQUIDITY', 'swapProfit', safeLiquidity.swapProfit);
  const displayLiqInc = pickUkSection('LIQUIDITY', 'otherIncome', safeLiquidity.otherIncome);
  const displayExDisp = pickUkSection('EXCHANGE', 'swapProfit', safeExchange.swapProfit);
  const displayExInc = pickUkSection('EXCHANGE', 'otherIncome', safeExchange.otherIncome);

  const totalSwapProfit =
    isUK && ukSummary
      ? Number(ukSummary.disposalNetFiat ?? ukSummary.disposalNetEUR ?? 0)
      : legacyTotalSwapProfit;

  const totalOtherIncome =
    isUK && ukSummary
      ? Number(
          ukSummary.miscellaneousIncomeFiat ??
            ukSummary.otherIncomeFiat ??
            ukSummary.otherIncomeEUR ??
            ukSummary.incomeFiat ??
            ukSummary.incomeEUR ??
            0
        )
      : legacyTotalOtherIncome;

  const currencySymbol = isUS ? '$' : isUK ? '£' : '€';
  const currencyCode = isUS ? 'USD' : isUK ? 'GBP' : 'EUR';

  const formatCurrency = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '—';
    // Non-breaking space: keeps symbol + amount on one line on narrow mobile widths.
    return `${currencySymbol}\u00A0${fmtEurSummary(num)}`;
  };

  // ✅ PDF mode: clean, monochrom, report-style layout
  if (pdfMode) {
    const rows = [
      { label: 'VET', disp: displayVetDisp, other: displayVetInc },
      { label: 'VTHO', disp: displayVthoDisp, other: displayVthoInc },
      { label: 'VIP-180', disp: displayB3trDisp, other: displayB3trInc },
      { label: 'NFT', disp: displayNftDisp, other: displayNftInc },
      { label: 'Liquidity', disp: displayLiqDisp, other: displayLiqInc },
      {
        label: cc === 'DE' ? 'Börsen-Importe' : 'Exchange',
        disp: displayExDisp,
        other: displayExInc,
      },
    ];

    // Länder-spezifische Kategorien und Thresholds
    const disposal = categoryText('disposal');
    const other = categoryText('other');

    const thresholdUi = getThresholdUi({
      countryCode: selectedCountry,
      year: selectedYear,
      language
    });

    return (
      <div className="pdf-dashboard">
        <h2 className="pdf-dashboard-title">
          {language === "en" ? "Tax overview" : "Steuerübersicht"}
        </h2>
        <div className="pdf-dashboard-sub">
          {language === "en"
            ? `Totals by category · Tax year ${displayYearLabel}`
            : `Summen nach Kategorie · Steuerjahr ${displayYearLabel}`}
        </div>

        <div className="pdf-dashboard-topgrid">
          <div className="pdf-box">
            <div className="pdf-box-title">
              {disposal.primary}
            </div>
            <div className="pdf-box-value">{formatCurrency(totalSwapProfit)}</div>
            {thresholdUi.mode === 'separate' && thresholdUi.disposalLabel ? (
              <div className="pdf-box-note">
                {thresholdUi.disposalLabel}
              </div>
            ) : thresholdUi.mode === 'combined' && thresholdUi.combinedLabel ? (
              <div className="pdf-box-note">
                {thresholdUi.combinedLabel}
              </div>
            ) : (
              <div className="pdf-box-note">
                {language === "en" ? "Exemption threshold: n/a" : "Freigrenze: n/a"}
              </div>
            )}
          </div>

          <div className="pdf-box">
            <div className="pdf-box-title">
              {other.primary}
            </div>
            <div className="pdf-box-value">{formatCurrency(totalOtherIncome)}</div>
            {thresholdUi.mode === 'separate' && thresholdUi.otherLabel ? (
              <div className="pdf-box-note">
                {thresholdUi.otherLabel}
              </div>
            ) : thresholdUi.mode === 'combined' && thresholdUi.combinedLabel ? (
              <div className="pdf-box-note">
                {thresholdUi.combinedLabel}
              </div>
            ) : (
              <div className="pdf-box-note">
                {language === "en" ? "Exemption threshold: n/a" : "Freigrenze: n/a"}
              </div>
            )}
          </div>
        </div>

        <table className="pdf-dashboard-table">
          <thead>
            <tr>
              <th>{language === "en" ? "Category" : "Kategorie"}</th>
              <th>{disposal.primary}</th>
              <th>{other.primary}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <td className="pdf-dashboard-td-label">{r.label}</td>
                <td className="pdf-dashboard-td-num">{formatCurrency(r.disp)}</td>
                <td className="pdf-dashboard-td-num">{formatCurrency(r.other)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Mobile embedded: „Kapitalgewinne/-Verluste“ + Betrag oben, „(Form …)“ darunter (kein Textumbruch).
  const splitMetricLabel = (primary, secondary) => {
    if (secondary) {
      return {
        headline: String(primary || "").trim(),
        detail: String(secondary).trim(),
      };
    }
    const text = String(primary || "").trim();
    const parenMatch = text.match(/\s*(\([^)]+\))\s*$/);
    if (parenMatch) {
      return {
        headline: text.slice(0, parenMatch.index).trim(),
        detail: parenMatch[1].trim(),
      };
    }
    return { headline: text, detail: null };
  };

  // ✨ UI — Premium Dashboard (Analysis + Mobile „Herzstück“)
  const MetricRow = ({ label, labelSecondary, value, valueClass }) => {
    const { headline, detail } = splitMetricLabel(label, labelSecondary);

    if (embedded) {
      return (
        <div
          className={
            "group/row -mx-1.5 rounded-lg px-2 py-2.5 transition-all duration-200 ease-out hover:bg-slate-50/90"
          }
          title={label}
        >
          <div className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 flex-1 pr-1 text-[13px] font-medium leading-snug text-slate-700">
              {headline}
            </span>
            <span className={valueClass}>{value}</span>
          </div>
          {detail ? (
            <p className="mt-1 text-xs leading-snug text-slate-500">{detail}</p>
          ) : null}
        </div>
      );
    }

    return (
      <div className={ui.row} title={label}>
        <div className="min-w-0 flex flex-col pr-2">
          <span className="text-[13px] text-slate-600">{label}</span>
          {labelSecondary && (
            <span className="mt-0.5 text-xs leading-snug text-slate-500">{labelSecondary}</span>
          )}
        </div>
        <span className={valueClass}>{value}</span>
      </div>
    );
  };

  const SummaryCategoryHeader = ({
    categoryUi,
    icon: Icon,
    detailToneClass,
  }) => {
    const { headline, detail } = splitMetricLabel(
      categoryUi.primary,
      categoryUi.secondary
    );

    if (embedded) {
      return (
        <div className="mb-5 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-[15px] font-semibold leading-snug text-white/95 sm:text-base">
              {headline}
            </h3>
            {detail ? (
              <p className={`mt-1 text-xs leading-snug ${detailToneClass}`}>{detail}</p>
            ) : null}
          </div>
          {Icon ? (
            <div className={ui.summaryIconWrap} aria-hidden>
              <Icon className="h-5 w-5 text-white" strokeWidth={2} />
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <div className="mb-5 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-[15px] font-semibold leading-snug text-white/95 sm:text-base">
            {categoryUi.primary}
          </h3>
          {categoryUi.secondary && (
            <span className={`mt-1 block text-xs leading-snug ${detailToneClass}`}>
              {categoryUi.secondary}
            </span>
          )}
        </div>
        {Icon ? (
          <div className={ui.summaryIconWrap} aria-hidden>
            <Icon className="h-5 w-5 text-white" strokeWidth={2} />
          </div>
        ) : null}
      </div>
    );
  };

  // Länder-spezifische Kategorien (für UI-Modus)
  const disposalUi = categoryText('disposal');
  const otherUi = categoryText('other');

  const thresholdUiNonPdf = getThresholdUi({
    countryCode: selectedCountry,
    year: selectedYear,
    language
  });

  const TokenCard = ({ title, disposalValue, otherValue, icon: Icon }) => (
    <div className={ui.tokenWrap}>
      <div className={ui.tokenCard} tabIndex={0}>
        <div className="relative">
          <div className={ui.tokenTitleRow}>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold leading-snug tracking-tight text-slate-900">
                {title}
              </h3>
            </div>
            {Icon ? (
              <div className={ui.tokenIconWrap} aria-hidden>
                <Icon className="h-4 w-4" strokeWidth={2} />
              </div>
            ) : null}
          </div>

          <div className="space-y-0">
            <div className={ui.rowDivider}>
              <MetricRow
                label={disposalUi.primary}
                labelSecondary={disposalUi.secondary}
                value={formatCurrency(disposalValue)}
                valueClass={ui.valueBlue}
              />
            </div>
            <MetricRow
              label={otherUi.primary}
              labelSecondary={otherUi.secondary}
              value={formatCurrency(otherValue)}
              valueClass={ui.valueGreen}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const shellClass = embedded ? "relative w-full bg-transparent" : ui.shell;
  const contentClass = embedded
    ? "px-4 py-6 sm:px-6 sm:py-8"
    : "mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-10";

  return (
    <div className={shellClass}>
      <div className={embedded ? ui.embeddedFrame : ""}>
      {showHeader && (
        <div className="border-b border-slate-200/80 bg-gradient-to-b from-white via-white to-slate-50/50">
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 flex-1 text-center sm:text-left">
                <div className="mb-2 inline-flex items-center justify-center gap-2 sm:justify-start">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200/90 bg-slate-50 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                    <LayoutDashboard className="h-3.5 w-3.5 text-indigo-500" aria-hidden />
                    {language === "en" ? "Overview" : "Übersicht"}
                  </span>
                </div>
                <h1 className="text-balance text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
                  {tr("dashboardTitle", "Steuerübersicht Dashboard")}
                </h1>
                <p className="mt-2 text-pretty text-sm text-slate-600 sm:text-base">
                  {tr("dashboardSubtitle", "Alle berechneten Werte auf einen Blick")}{" "}
                  <span className="whitespace-nowrap text-slate-500">
                    · {tr("taxYear", "Steuerjahr")}{" "}
                    <span className="font-semibold tabular-nums text-slate-800">{displayYearLabel}</span>
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={contentClass}>
        {/* Summary Cards */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 ${compact ? "gap-4 mb-6" : "gap-6 mb-8"}`}>
          {/* Disposal */}
          <div
            className={`${ui.summaryCard} bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-700`}
          >
            <div className="relative">
              <SummaryCategoryHeader
                categoryUi={disposalUi}
                icon={TrendingUp}
                detailToneClass="text-indigo-100/85"
              />

              <p className={`${ui.summaryValue} mb-2 whitespace-nowrap`}>
                {formatCurrency(totalSwapProfit)}
              </p>
              <p className="text-sm text-indigo-100/90">
                {tr("disposalTransactionsTotal", "Veräußerungsgeschäfte Gesamt")}
              </p>

              {thresholdUiNonPdf.mode === "separate" && thresholdUiNonPdf.disposalLabel && (
                <p className="mt-3 text-xs leading-relaxed text-indigo-100/95">
                  <span className="mr-1 opacity-80">✶</span>
                  {thresholdUiNonPdf.disposalLabel}
                </p>
              )}
              {thresholdUiNonPdf.mode === "combined" && thresholdUiNonPdf.combinedLabel && (
                <p className="mt-3 text-xs leading-relaxed text-indigo-100/95">
                  <span className="mr-1 opacity-80">✶</span>
                  {thresholdUiNonPdf.combinedLabel}
                </p>
              )}
              {thresholdUiNonPdf.mode === "none" && (
                <p className="mt-3 text-xs text-indigo-100/90">
                  <span className="mr-1 opacity-80">✶</span>
                  {language === "en" ? "No tax-free threshold" : "Keine Freigrenze"}
                </p>
              )}
              {disposalUi.note && (
                <p className="mt-2 text-xs italic text-indigo-100/75">{disposalUi.note}</p>
              )}
            </div>
          </div>

          {/* Other Income */}
          <div
            className={`${ui.summaryCard} bg-gradient-to-br from-emerald-600 via-teal-600 to-emerald-800`}
          >
            <div className="relative">
              <SummaryCategoryHeader
                categoryUi={otherUi}
                icon={Coins}
                detailToneClass="text-emerald-100/85"
              />

              <p className={`${ui.summaryValue} mb-2 whitespace-nowrap`}>
                {formatCurrency(totalOtherIncome)}
              </p>
              <p className="text-sm text-emerald-100/90">
                {tr("otherIncomeTotal", "Sonstige Einkünfte Gesamt")}
              </p>

              {thresholdUiNonPdf.mode === "separate" && thresholdUiNonPdf.otherLabel && (
                <p className="mt-3 text-xs leading-relaxed text-emerald-100/95">
                  <span className="mr-1 opacity-80">✶</span>
                  {thresholdUiNonPdf.otherLabel}
                </p>
              )}
              {thresholdUiNonPdf.mode === "combined" && thresholdUiNonPdf.combinedLabel && (
                <p className="mt-3 text-xs leading-relaxed text-emerald-100/95">
                  <span className="mr-1 opacity-80">✶</span>
                  {thresholdUiNonPdf.combinedLabel}
                </p>
              )}
              {thresholdUiNonPdf.mode === "none" && (
                <p className="mt-3 text-xs text-emerald-100/90">
                  <span className="mr-1 opacity-80">✶</span>
                  {language === "en" ? "No tax-free threshold" : "Keine Freigrenze"}
                </p>
              )}
              {otherUi.note && (
                <p className="mt-2 text-xs italic text-emerald-100/75">{otherUi.note}</p>
              )}
            </div>
          </div>
        </div>

        {/* 6 Cards */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 ${compact ? "gap-4" : "gap-5 sm:gap-6"}`}>
          <TokenCard
            icon={CircleDot}
            title={tr("vetTransactions", "VET Transaktionen")}
            disposalValue={displayVetDisp}
            otherValue={displayVetInc}
          />

          <TokenCard
            icon={Zap}
            title={tr("vthoTransactions", "VTHO Transaktionen")}
            disposalValue={displayVthoDisp}
            otherValue={displayVthoInc}
          />

          <TokenCard
            icon={Sparkles}
            title={tr("b3trTransactions", "VIP-180 Token-Transaktionen")}
            disposalValue={displayB3trDisp}
            otherValue={displayB3trInc}
          />

          <TokenCard
            icon={ImageIcon}
            title={tr("nftTransactions", "NFT Transaktionen")}
            disposalValue={displayNftDisp}
            otherValue={displayNftInc}
          />

          <TokenCard
            icon={Droplets}
            title={tr("liquidityPools", language === "en" ? "Liquidity Pools" : "Liquidity Pools")}
            disposalValue={displayLiqDisp}
            otherValue={displayLiqInc}
          />

          <TokenCard
            icon={ArrowLeftRight}
            title={tr("exchangeTrades", language === "en" ? "Exchange Trades" : "Exchange Trades")}
            disposalValue={displayExDisp}
            otherValue={displayExInc}
          />
        </div>
      </div>
      </div>
    </div>
  );
}